import type {
  ContractObjectShareView,
  ContractOrgInvitationSummary,
  ContractVersionSummary,
} from "@closer/shared";
import { OBJECT_SHARE_PERMISSION_NAMES } from "@closer/shared";
import { Badge, Button, Card, FormField, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

const NESNE_TURU_ADLARI: Record<ContractObjectShareView["objectType"], string> = {
  version: "Sürüm",
  clause: "Madde",
  work_item: "Çalışma kalemi",
  file: "Dosya",
  revision: "Revizyon önerisi",
  negotiation: "Müzakere",
};
const PAYLASIM_DURUM_ADLARI: Record<ContractObjectShareView["status"], string> = {
  active: "Açık",
  revoked: "İptal edildi",
  superseded: "Yerini yeni kapsam aldı",
};
function izinMetni(izinler: readonly string[]): string {
  return izinler
    .map((k) => OBJECT_SHARE_PERMISSION_NAMES[k as keyof typeof OBJECT_SHARE_PERMISSION_NAMES] ?? k)
    .join(", ");
}

/**
 * KARŞI TARAFA AÇILAN SÜRÜMLER (CLOS-250/251 yeteneği · CLOS-1128 · Halit K1 REVİZE).
 *
 * ### ⚠ BELGE sekmesinde, Taraflar'da DEĞİL
 *
 * Taraf kartının işi hukuki taraf kimliği, davet, iş birliği durumu ve yetkililer; bir
 * sürümü karşı tarafa görüntüleme/yorum kapsamıyla açmak ise kimlik yönetimi değil,
 * belge/versiyon bazlı bir iş birliği eylemi. Kanonik karar revizyon/müzakere/versiyon iş
 * birliğini Belge > Revizyon Önerileri / Müzakere yüzeyine koyuyor; bu bölüm onun hemen
 * altında.
 *
 * ⚠ Alıcı yalnız daveti KABUL ETMİŞ hukuki taraf organizasyonudur; genel organizasyon
 * seçimi ya da ham kimlik girişi YOK. Yönetim kapısı sunucudan (`sharing-context.canManage`);
 * yönetemeyen kullanıcıya bölüm çizilmez ve davet/paylaşım listeleri hiç okunmaz.
 */
export function ContractObjectSharesSection({ contractId }: { contractId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [paylasimHedefi, setPaylasimHedefi] = useState("");
  const [paylasimVersiyonu, setPaylasimVersiyonu] = useState("");
  const [kapsamDuzenlenen, setKapsamDuzenlenen] = useState<string | null>(null);
  const [kapsamIzinleri, setKapsamIzinleri] = useState<"view" | "view,comment">("view");
  const [kapsamGecmisi, setKapsamGecmisi] = useState<"none" | "full">("none");
  const [gecmisAcik, setGecmisAcik] = useState<string | null>(null);

  const davetAnahtari = tenantKey(tenantId, "contract", contractId, "org-davetleri");
  const paylasimAnahtari = tenantKey(tenantId, "contract", contractId, "nesne-paylasimlari");
  const baglam = useQuery({
    enabled: can("contract.view"),
    queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-baglami"),
    queryFn: async () =>
      (
        await api<{ canManage: boolean }>(`/api/contracts/${contractId}/sharing-context`, {
          tenantId,
        })
      ).data,
  });
  const yonetebilir = baglam.data?.canManage ?? false;
  const davetler = useQuery({
    enabled: yonetebilir,
    queryKey: davetAnahtari,
    queryFn: async () =>
      (
        await api<ContractOrgInvitationSummary[]>(`/api/contracts/${contractId}/org-invitations`, {
          tenantId,
        })
      ).data,
  });
  const paylasimlar = useQuery({
    enabled: yonetebilir,
    queryKey: paylasimAnahtari,
    queryFn: async () =>
      (
        await api<ContractObjectShareView[]>(`/api/contracts/${contractId}/object-shares`, {
          tenantId,
        })
      ).data,
  });
  const versiyonlar = useQuery({
    enabled: yonetebilir,
    queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-versiyonlari"),
    queryFn: async () =>
      (
        await api<ContractVersionSummary[]>(`/api/contracts/${contractId}/versions?limit=50`, {
          tenantId,
        })
      ).data,
  });
  const gecmis = useQuery({
    enabled: gecmisAcik !== null,
    queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-gecmisi", gecmisAcik ?? ""),
    queryFn: async () =>
      (
        await api<ContractObjectShareView[]>(
          `/api/contracts/${contractId}/object-shares/${gecmisAcik}/history`,
          { tenantId },
        )
      ).data,
  });
  const versiyonPaylas = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/object-shares`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          objectType: "version",
          objectId: paylasimVersiyonu,
          versionId: paylasimVersiyonu,
          targetTenantId: paylasimHedefi,
          permissions: ["view", "comment"],
          includeHistory: "none",
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: paylasimAnahtari });
      toast.success("Sürüm paylaşıldı");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Paylaşım yapılamadı"),
  });
  const paylasimIptal = useMutation({
    mutationFn: async (shareId: string) =>
      api(`/api/contracts/${contractId}/object-shares/${shareId}/revoke`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: paylasimAnahtari });
      toast.success("Paylaşım iptal edildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Paylaşım iptal edilemedi"),
  });
  const kapsamGuncelle = useMutation({
    mutationFn: async (shareId: string) =>
      api(`/api/contracts/${contractId}/object-shares/${shareId}`, {
        method: "PATCH",
        tenantId,
        body: JSON.stringify({
          permissions: kapsamIzinleri.split(","),
          includeHistory: kapsamGecmisi,
        }),
      }),
    onSuccess: async (_veri, shareId) => {
      setKapsamDuzenlenen(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: paylasimAnahtari }),
        queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", contractId, "paylasim-gecmisi", shareId),
        }),
      ]);
      toast.success("Paylaşım kapsamı güncellendi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Paylaşım kapsamı güncellenemedi"),
  });

  if (!can("contract.view") || !yonetebilir) return null;
  const katilimcilar = (davetler.data ?? []).filter((d) => d.status === "accepted");

  return (
    <Card>
      <section className="flex flex-col gap-2" data-testid="karsi-tarafa-acilan-surumler">
        <h2 className="text-base font-semibold text-fg-heading">Karşı Tarafa Açılan Sürümler</h2>
        <p className="text-xs text-fg-soft">
          Belirli bir sürümü, daveti kabul etmiş bir hukuki taraf organizasyonuna görüntüleme ya da
          yorum kapsamıyla açar. Kapsam değişikliği yeni bir kayıt açar; geçmiş korunur.
        </p>
        {paylasimlar.isPending ? (
          <Skeleton lines={2} />
        ) : (paylasimlar.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-muted">Karşı tarafa açılmış bir sürüm yok.</p>
        ) : (
          <ul className="flex flex-col">
            {(paylasimlar.data ?? []).map((paylasim) => (
              <li
                key={paylasim.id}
                className="flex flex-col gap-2 border-b border-border-default py-1.5 last:border-0"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm text-fg">
                      {NESNE_TURU_ADLARI[paylasim.objectType]}
                    </span>
                    <span className="text-xs text-fg-soft">
                      {izinMetni(paylasim.permissions)} · {paylasim.sharedByName} ·{" "}
                      {new Date(paylasim.createdAt).toLocaleDateString("tr-TR")}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge tone={paylasim.status === "active" ? "success" : "neutral"}>
                      {PAYLASIM_DURUM_ADLARI[paylasim.status]}
                    </Badge>
                    {/* Kapsam değişikliği yalnız AÇIK paylaşımda (CLOS-251); uç da reddeder. */}
                    {paylasim.status === "active" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setKapsamDuzenlenen(
                            kapsamDuzenlenen === paylasim.id ? null : paylasim.id,
                          );
                          setKapsamIzinleri(
                            paylasim.permissions.includes("comment") ? "view,comment" : "view",
                          );
                          setKapsamGecmisi(paylasim.includeHistory);
                        }}
                      >
                        Kapsamı değiştir
                      </Button>
                    ) : null}
                    {/* Geçmişin kapısı ayrı (`contract.history`); iptal edilmiş satırın da zinciri okunur. */}
                    {can("contract.history") ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setGecmisAcik(gecmisAcik === paylasim.id ? null : paylasim.id)
                        }
                      >
                        {gecmisAcik === paylasim.id ? "Geçmişi gizle" : "Geçmiş"}
                      </Button>
                    ) : null}
                    {paylasim.status === "active" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => paylasimIptal.mutate(paylasim.id)}
                      >
                        Paylaşımı kaldır
                      </Button>
                    ) : null}
                  </span>
                </div>
                {kapsamDuzenlenen === paylasim.id ? (
                  <div className="flex flex-col gap-2 rounded-md bg-muted p-2">
                    <p className="text-xs text-fg-soft">
                      Kapsam değişikliği bu kaydın üzerine yazmaz: yeni bir revizyon açılır, önceki
                      kayıt geçmişte kalır.
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-48 flex-1">
                        <FormField label="İzin kapsamı">
                          <Select
                            id={`paylasim-izin-${paylasim.id}`}
                            value={kapsamIzinleri}
                            onChange={(olay) =>
                              setKapsamIzinleri(olay.target.value as "view" | "view,comment")
                            }
                          >
                            <option value="view">Görüntüleme</option>
                            <option value="view,comment">Görüntüleme ve yorum</option>
                          </Select>
                        </FormField>
                      </div>
                      <div className="min-w-48 flex-1">
                        <FormField label="Geçmiş erişimi">
                          <Select
                            id={`paylasim-gecmis-${paylasim.id}`}
                            value={kapsamGecmisi}
                            onChange={(olay) =>
                              setKapsamGecmisi(olay.target.value as "none" | "full")
                            }
                          >
                            <option value="none">Geçmiş kapalı</option>
                            <option value="full">Geçmiş dahil</option>
                          </Select>
                        </FormField>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        loading={kapsamGuncelle.isPending}
                        onClick={() => kapsamGuncelle.mutate(paylasim.id)}
                      >
                        Kapsamı kaydet
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setKapsamDuzenlenen(null)}
                      >
                        Vazgeç
                      </Button>
                    </div>
                  </div>
                ) : null}
                {gecmisAcik === paylasim.id ? (
                  <div className="rounded-md bg-muted p-2">
                    {gecmis.isPending ? (
                      <Skeleton lines={2} />
                    ) : gecmis.error ? (
                      <StateView
                        state={resolveErrorState(gecmis.error, {
                          idempotent: true,
                          overrides: {
                            error: {
                              title: "Paylaşım geçmişi yüklenemedi",
                              description: "Zincir getirilemedi.",
                            },
                          },
                        })}
                        onRetry={() => void gecmis.refetch()}
                      />
                    ) : (
                      <ul data-testid={`paylasim-gecmisi-${paylasim.id}`} className="flex flex-col">
                        {(gecmis.data ?? []).length === 0 ? (
                          <li className="text-xs text-fg-muted">Bu paylaşımın geçmişi yok.</li>
                        ) : (
                          (gecmis.data ?? []).map((kayit) => (
                            <li
                              key={kayit.id}
                              className="border-b border-border-default py-1 text-xs text-fg-soft last:border-0"
                            >
                              {kayit.sharedByName} · {izinMetni(kayit.permissions)} ·{" "}
                              {kayit.includeHistory === "full" ? "Geçmiş dahil" : "Geçmiş kapalı"} ·{" "}
                              {PAYLASIM_DURUM_ADLARI[kayit.status]} ·{" "}
                              {new Date(kayit.createdAt).toLocaleString("tr-TR")}
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {katilimcilar.length === 0 ? (
          <p className="text-xs text-fg-soft">
            Paylaşım için önce bir hukuki tarafın daveti kabul etmesi gerekir.
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-48 flex-1">
              <FormField label="Paylaşılacak sürüm">
                <Select
                  id="paylasim-versiyon"
                  value={paylasimVersiyonu}
                  onChange={(olay) => setPaylasimVersiyonu(olay.target.value)}
                >
                  <option value="">Seçiniz</option>
                  {(versiyonlar.data ?? []).map((v) => (
                    <option key={v.id} value={v.id}>
                      Sürüm {v.versionNumber}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <div className="min-w-48 flex-1">
              <FormField label="Hukuki taraf organizasyonu">
                <Select
                  id="paylasim-hedef"
                  value={paylasimHedefi}
                  onChange={(olay) => setPaylasimHedefi(olay.target.value)}
                >
                  <option value="">Seçiniz</option>
                  {katilimcilar.map((k) => (
                    <option key={k.id} value={k.targetTenantId ?? ""}>
                      {k.targetOrganizationName}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => versiyonPaylas.mutate()}
              loading={versiyonPaylas.isPending}
              disabled={paylasimVersiyonu === "" || paylasimHedefi === ""}
            >
              Sürümü paylaş
            </Button>
          </div>
        )}
      </section>
    </Card>
  );
}
